import React, { useCallback } from "react";
import { Modal } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import { Alert, Button, HookFormInputGroup, SpinnerLoader } from "components";
import { ValidationMessages } from "globalConstants";
import { getErrorMessage } from "helpers";
import { useInitializeEmailChangeMutation } from "queries";
import { uiActions } from "store";
import { getCurrentUserRole } from "store/user/user.reducer";
import {
	emailValid,
	required,
	trimNormalizer,
	trimMidSentenceNormalizer,
	validateEmailForUserType,
} from "validators";

import styles from "../modal.module.scss";

type EditEmailModalProps = {
	show: boolean;
	onHide: () => void;
	currentEmail: string;
	allowedDomains: string[];
};

const EditEmailModal = ({
	show,
	onHide,
	currentEmail,
	allowedDomains,
}: EditEmailModalProps) => {
	const dispatch = useDispatch();

	const role = useSelector(getCurrentUserRole);

	const formMethods = useForm({
		mode: "onChange",
		reValidateMode: "onChange",
		defaultValues: {
			email: "",
		},
	});

	const {
		register,
		handleSubmit,
		setError,
		formState: { errors, isDirty },
	} = formMethods;

	const {
		mutate: initializeEmailChange,
		isLoading: isLoadingInitializeEmailChange,
	} = useInitializeEmailChangeMutation({
		onSuccess: onHide,
		onError: error => {
			if (error?.title === "email_domain_is_not_whitelisted") {
				setError("email", {
					message: ValidationMessages.NotAllowedEmail,
				});
			} else if (error?.title === "user_email_already_exists") {
				setError("email", {
					message: ValidationMessages.EmailExist,
				});
			} else {
				dispatch(
					uiActions.showErrorNotification({
						title: getErrorMessage(error),
					}),
				);
			}
		},
	});

	const handleSubmitForm = ({ email }: { email: string }) => {
		if (currentEmail === email) {
			setError("email", {
				message: ValidationMessages.EmailExist,
			});
		} else {
			initializeEmailChange({ email });
		}
	};

	const validateEmailForUserTypeFn = useCallback(
		(value: string) =>
			validateEmailForUserType({
				email: value,
				role: role?.name,
			}),
		[role],
	);

	const handleClick = e => e.stopPropagation();

	const isLoading = isLoadingInitializeEmailChange;

	return (
		<Modal show={show} onHide={onHide} centered>
			<Modal.Header closeButton>
				<Modal.Title as="h6" className="text-center">
					<div className={styles.title}>
						Change{" "}
						<span className={styles.changesEntityTitle}>
							{currentEmail}
						</span>{" "}
						Address
					</div>
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className={styles.verificationModalBody}>
					<FormProvider {...formMethods}>
						<form onSubmit={handleSubmit(handleSubmitForm)}>
							<HookFormInputGroup
								name="email"
								register={register}
								validator={{
									required,
									emailValid,
									validateEmailForUserTypeFn,
								}}
								colInput={12}
								errors={errors}
								normalizeOnBlur={trimNormalizer}
								normalize={trimMidSentenceNormalizer}
								showRequiredMark
								loading={isLoading}
								disabled={isLoading}
								loader={SpinnerLoader}
								placeholder="Enter email address"
							/>
							{allowedDomains?.length > 0 && (
								<Alert
									variant="notice"
									message={allowedDomains.join(", ")}
								/>
							)}
							<div className={styles.emailVerificationButtonWrap}>
								<Button
									type="submit"
									value="Send a Verification Email"
									variant="primary"
									disabled={!isDirty || isLoading}
									onClick={handleClick}
								/>
							</div>
						</form>
					</FormProvider>
				</div>
			</Modal.Body>
		</Modal>
	);
};

export default EditEmailModal;
